import React, { useEffect, useRef, useState } from "react";
import technologyVideo from "./../assets/technology.mp4";
import { Link as ScrollLink } from "react-scroll";
import webtech from "./../assets/web technology.jpg";
import ecommerce from "./../assets/ecommerce.jpg";
import softwareTech from "./../assets/software development.jpg";
import mobileApp from "./../assets/mobile development.jpg";
import cloudSolutions from "./../assets/cloud.jpg";
import dataAnalytics from "./../assets/Data Analytics.jpg";
import favicon from "../assets/favicon.ico";
import { Helmet } from 'react-helmet';
import SchemaMarkupComponent from "../components/SchemaMarkupComponent.jsx";

// Neon Text Effect Component
const NeonText = ({ text, color = "#6f4cff", size = "text-3xl", className = "" }) => {
  return (
    <h2 className={`${size} font-bold ${className}`} style={{
      color: "white",
      textShadow: `0 0 5px ${color}, 0 0 10px ${color}, 0 0 15px ${color}, 0 0 20px ${color}`,
      animation: "neonPulse 2s infinite alternate"
    }}>
      {text}
    </h2>
  );
};

// Glowing Button Component
const GlowButton = ({ text, href, primary = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <a
      href={href}
      className={`relative overflow-hidden rounded-full px-6 py-3 font-semibold text-base transition-all duration-300 ${
        primary 
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
          : 'bg-transparent border-2 border-white text-white'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={`${text} - Techecosolution`}
    >
      <span className="relative z-10">{text}</span>
      
      <div 
        className={`absolute inset-0 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        style={{
          background: primary 
            ? 'radial-gradient(circle at center, rgba(111, 76, 255, 0.8) 0%, rgba(111, 76, 255, 0) 70%)' 
            : 'radial-gradient(circle at center, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 70%)',
          filter: 'blur(15px)',
          transform: 'scale(1.2)'
        }}
      ></div>
      
      <div 
        className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20"
        style={{
          animation: isHovered ? 'shine 1.5s infinite' : 'none'
        }}
      ></div>
    </a>
  );
};

// Interactive Card Component with Glow Effect
const GlowingCard = ({ children, color = "#6f4cff" }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      className="relative rounded-xl overflow-hidden transition-all duration-300 transform hover:-translate-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black opacity-90 z-0" />
      
      <div 
        className="absolute -inset-2 rounded-xl transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at center, ${color}40 0%, ${color}00 70%)`,
          filter: 'blur(20px)',
          opacity: isHovered ? 1 : 0.3,
          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        }}
      />
      
      <div 
        className="absolute inset-0 rounded-xl border border-opacity-30 z-10"
        style={{
          borderColor: color,
          boxShadow: isHovered ? `0 0 20px ${color}40` : 'none'
        }}
      />
      
      <div className="relative z-20 p-6">
        {children}
      </div>
    </div>
  );
};

// 3D Tilt Card with Glow Effect
const TiltGlowCard = ({ children, color = "#6f4cff" }) => {
  const cardRef = useRef(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const card = cardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateXValue = (y - centerY) / 20;
    const rotateYValue = (centerX - x) / 20;
    
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };
  
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  
  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };
  
  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative rounded-xl overflow-hidden"
      style={{
        transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        transition: 'transform 0.1s ease-out'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black opacity-90 z-0" />
      
      <div 
        className="absolute -inset-2 rounded-xl transition-all duration-300"
        style={{
          background: `radial-gradient(circle at center, ${color}40 0%, ${color}00 70%)`,
          filter: 'blur(20px)',
          opacity: isHovered ? 1 : 0.3,
          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        }}
      />
      
      <div 
        className="absolute inset-0 rounded-xl border border-opacity-30 z-10"
        style={{
          borderColor: color,
          boxShadow: isHovered ? `0 0 20px ${color}40` : 'none'
        }}
      />
      
      <div className="relative z-20 p-6">
        {children}
      </div>
      
      {isHovered && (
        <div 
          className="absolute inset-0 z-30 opacity-10"
          style={{
            background: `linear-gradient(45deg, transparent, ${color}, transparent)`,
            backgroundSize: '200% 200%',
            animation: 'shine 1.5s infinite'
          }}
        ></div>
      )}
    </div>
  );
};

const TechnologyPage = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const metaUrl = "https://www.techecosolution.in/technology-services";
  const currentDate = new Date().toISOString();
  
  // Multiple section refs for better animations
  const heroRef = useRef(null);
  const webRef = useRef(null);
  const ecommerceRef = useRef(null);
  const softwareRef = useRef(null);
  const mobileRef = useRef(null);
  const cloudRef = useRef(null);
  const dataRef = useRef(null);
  const whyUsRef = useRef(null);

  // Services data for schema markup
  const services = [
    {
      name: "Web Development",
      description: "Transforming ideas into visually stunning and highly functional websites with cutting-edge designs ensuring seamless user interaction across all devices.",
      image: webtech,
      category: "Web Development"
    },
    {
      name: "E-Commerce Development",
      description: "Powerful online stores with secure payment gateways, inventory management, and conversion-optimized design.",
      image: ecommerce,
      category: "E-Commerce"
    },
    {
      name: "Software Development",
      description: "Custom software solutions designed to fit your specific business requirements for enhanced efficiency and scalability.",
      image: softwareTech,
      category: "Software Development"
    },
    {
      name: "Mobile App Development",
      description: "Bespoke mobile solutions for iOS, Android, and hybrid frameworks with visually appealing and user-friendly interfaces.",
      image: mobileApp,
      category: "Mobile Development"
    },
    {
      name: "Cloud Solutions",
      description: "Flexible cloud environments with 99.9% uptime, disaster recovery, and enterprise-grade security standards.",
      image: cloudSolutions,
      category: "Cloud Computing"
    },
    {
      name: "Data Analytics",
      description: "Turn raw data into valuable insights using AI and machine learning to analyze complex data patterns and forecast trends.",
      image: dataAnalytics,
      category: "Data Analytics"
    }
  ];

  // Enhanced Schema Markup for Technology Services
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Techecosolution (TES)",
    "alternateName": "TES",
    "url": metaUrl,
    "logo": `https://www.techecosolution.in${favicon}`,
    "description": "Leading technology solutions provider specializing in web development, software development, mobile apps, cloud solutions, and data analytics services in India",
    "foundingDate": "2020",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bangalore",
      "addressRegion": "Karnataka",
      "addressCountry": "IN"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-XXXXXXXXXX",
      "contactType": "customer service",
      "availableLanguage": ["English", "Hindi"],
      "areaServed": "IN"
    },
    "sameAs": [
      "https://www.facebook.com/techecosolution",
      "https://www.linkedin.com/company/techecosolution",
      "https://twitter.com/techecosolution"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "200"
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Technology Solutions - Techecosolution",
    "url": metaUrl,
    "description": "Comprehensive technology solutions including web development, software development, mobile apps, cloud solutions, and data analytics services",
    "publisher": {
      "@type": "Organization",
      "name": "Techecosolution"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": services.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Service",
          "name": service.name,
          "description": service.description,
          "category": service.category,
          "image": `https://www.techecosolution.in${service.image}`,
          "provider": {
            "@type": "Organization",
            "name": "Techecosolution"
          }
        }
      }))
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Technology Solutions & Development Services",
    "description": "Complete technology solutions including web development, software development, mobile applications, cloud solutions, and data analytics services",
    "provider": {
      "@type": "Organization",
      "name": "Techecosolution",
      "url": "https://www.techecosolution.in"
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "serviceType": "Technology Services",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Technology Development Services",
      "itemListElement": services.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.name,
          "description": service.description,
          "category": service.category
        }
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.techecosolution.in/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Technology Solutions",
        "item": metaUrl
      }
    ]
  };

  // const faqSchema = {
  //   "@context": "https://schema.org",
  //   "@type": "FAQPage",
  //   "mainEntity": [
  //     {
  //       "@type": "Question",
  //       "name": "What technology services does Techecosolution offer?",
  //       "acceptedAnswer": {
  //         "@type": "Answer",
  //         "text": "Techecosolution offers comprehensive technology services including web development, software development, mobile app development, cloud solutions, data analytics, and e-commerce development."
  //       }
  //     },
  //     {
  //       "@type": "Question",
  //       "name": "Do you provide custom software development?",
  //       "acceptedAnswer": {
  //         "@type": "Answer",
  //         "text": "Yes, we provide custom software development solutions tailored to your specific business requirements with scalable architecture and seamless integration capabilities."
  //       }
  //     },
  //     {
  //       "@type": "Question",
  //       "name": "Which platforms do you develop mobile apps for?",
  //       "acceptedAnswer": {
  //         "@type": "Answer",
  //         "text": "We develop mobile applications for iOS, Android, and hybrid frameworks including Flutter and React Native, ensuring cross-platform compatibility."
  //       }
  //     }
  //   ]
  // };

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
    
    // Enhanced SEO meta tags
    document.title = "Web, Software Development & Mobile Apps | Techecosolution";
    
    // Meta description
    const metaDescription = document.querySelector("meta[name='description']");
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Techecosolution offers cutting-edge technology solutions including web development, software development, mobile apps, cloud solutions, and data analytics. Transform your business with innovative tech solutions in India."
      );
    }

    // Meta keywords
    const metaKeywords = document.querySelector("meta[name='keywords']");
    if (metaKeywords) {
      metaKeywords.setAttribute(
        "content",
        "technology solutions, web development, software development, mobile app development, cloud solutions, data analytics, e-commerce development, custom software, Techecosolution, technology services India, digital transformation"
      );
    }

    // Robots meta tag
    let robotsMeta = document.querySelector("meta[name='robots']");
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // Author meta tag
    let authorMeta = document.querySelector("meta[name='author']");
    if (!authorMeta) {
      authorMeta = document.createElement("meta");
      authorMeta.setAttribute("name", "author");
      document.head.appendChild(authorMeta);
    }
    authorMeta.setAttribute("content", "Techecosolution Team");

    // Canonical link
    const existingCanonical = document.querySelector("link[rel='canonical']");
    if (existingCanonical) {
      existingCanonical.setAttribute("href", metaUrl);
    } else {
      const canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      canonicalLink.setAttribute("href", metaUrl);
      document.head.appendChild(canonicalLink);
    }

    // Hreflang attributes
    const hreflangs = [
      { lang: "en", region: "in", url: metaUrl },
      { lang: "en", region: "us", url: metaUrl },
      { lang: "en", region: "gb", url: metaUrl },
      { lang: "x-default", region: "", url: metaUrl }
    ];

    // Remove existing hreflang links
    const existingHreflangs = document.querySelectorAll("link[hreflang]");
    existingHreflangs.forEach(link => link.remove());

    // Add new hreflang links
    hreflangs.forEach(({ lang, region, url }) => {
      const hreflangLink = document.createElement("link");
      hreflangLink.setAttribute("rel", "alternate");
      hreflangLink.setAttribute("hreflang", region ? `${lang}-${region}` : lang);
      hreflangLink.setAttribute("href", url);
      document.head.appendChild(hreflangLink);
    });

    // Favicon
    const existingFavicon = document.querySelector("link[rel='icon']");
    if (existingFavicon) {
      existingFavicon.setAttribute("href", favicon);
    } else {
      const faviconLink = document.createElement("link");
      faviconLink.setAttribute("rel", "icon");
      faviconLink.setAttribute("type", "image/x-icon");
      faviconLink.setAttribute("href", favicon);
      document.head.appendChild(faviconLink);
    }

    // Apple touch icon
    let appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement("link");
      appleTouchIcon.setAttribute("rel", "apple-touch-icon");
      appleTouchIcon.setAttribute("sizes", "180x180");
      appleTouchIcon.setAttribute("href", favicon);
      document.head.appendChild(appleTouchIcon);
    }

    // Preconnect to external domains
    const preconnectDomains = [
      "https://fonts.googleapis.com",
      "https://fonts.gstatic.com",
      "https://www.google-analytics.com"
    ];

    preconnectDomains.forEach(domain => {
      let preconnectLink = document.querySelector(`link[href="${domain}"]`);
      if (!preconnectLink) {
        preconnectLink = document.createElement("link");
        preconnectLink.setAttribute("rel", "preconnect");
        preconnectLink.setAttribute("href", domain);
        document.head.appendChild(preconnectLink);
      }
    });

    // DNS prefetch for performance
    const dnsPrefetchDomains = [
      "//www.googletagmanager.com",
      "//connect.facebook.net"
    ];

    dnsPrefetchDomains.forEach(domain => {
      let dnsPrefetchLink = document.querySelector(`link[href="${domain}"]`);
      if (!dnsPrefetchLink) {
        dnsPrefetchLink = document.createElement("link");
        dnsPrefetchLink.setAttribute("rel", "dns-prefetch");
        dnsPrefetchLink.setAttribute("href", domain);
        document.head.appendChild(dnsPrefetchLink);
      }
    });

    // Update active section based on scroll
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      if (heroRef.current && scrollY < heroRef.current.clientHeight) setActiveSection("hero");
      else if (webRef.current && scrollY < webRef.current.clientHeight) setActiveSection("web");
      else if (ecommerceRef.current && scrollY < ecommerceRef.current.clientHeight) setActiveSection("ecommerce");
      else if (softwareRef.current && scrollY < softwareRef.current.clientHeight) setActiveSection("software");
      else if (mobileRef.current && scrollY < mobileRef.current.clientHeight) setActiveSection("mobile");
      else if (cloudRef.current && scrollY < cloudRef.current.clientHeight) setActiveSection("cloud");
      else if (dataRef.current && scrollY < dataRef.current.clientHeight) setActiveSection("data");
      else if (whyUsRef.current && scrollY < whyUsRef.current.clientHeight) setActiveSection("whyUs");
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <SchemaMarkupComponent />
      <Helmet>
        {/* Primary Meta Tags */}
        <title>tes - Web, Software Development & Mobile Apps | Techecosolution</title>
        <meta name="title" content="tes - Web, Software Development & Mobile Apps | Techecosolution" />
        <meta name="description" content="Techecosolution offers cutting-edge technology solutions including web development, software development, mobile apps, cloud solutions, and data analytics. Transform your business with innovative tech solutions in India." />
        <meta name="keywords" content="web, software development, mobile app development, cloud solutions, data analytics, e-commerce development, custom software, Techecosolution, technology services India" />
        <meta name="author" content="Techecosolution Team" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="web" />
        <meta name="rating" content="general" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Techecosolution" />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:title" content="Web, Software Development & Mobile Apps" />
        <meta property="og:description" content="Techecosolution offers cutting-edge technology solutions including web development, software development, mobile apps, cloud solutions, and data analytics services in India." />
        <meta property="og:image" content={`https://www.techecosolution.in${webtech}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Techecosolution - Development Services" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:locale:alternate" content="en_US" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:site" content="@techecosolution" />
        <meta property="twitter:creator" content="@techecosolution" />
        <meta property="twitter:url" content={metaUrl} />
        <meta property="twitter:title" content="Web, Software Development & Mobile Apps" />
        <meta property="twitter:description" content="Techecosolution offers cutting-edge technology solutions including web development, software development, mobile apps, cloud solutions, and data analytics." />
        <meta property="twitter:image" content={`https://www.techecosolution.in${webtech}`} />
        <meta property="twitter:image:alt" content="Techecosolution Services" />

        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#6f4cff" />
        <meta name="msapplication-TileColor" content="#6f4cff" />
        <meta name="msapplication-config" content="/browserconfig.xml" />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="IN-KA" />
        <meta name="geo.placename" content="Bangalore" />
        <meta name="geo.position" content="12.9716;77.5946" />
        <meta name="ICBM" content="12.9716, 77.5946" />

        {/* Business/Organization Tags */}
        <meta name="business:contact_data:street_address" content="Bangalore, Karnataka" />
        <meta name="business:contact_data:locality" content="Bangalore" />
        <meta name="business:contact_data:region" content="Karnataka" />
        <meta name="business:contact_data:postal_code" content="560001" />
        <meta name="business:contact_data:country_name" content="India" />

        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {/* <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script> */}

        {/* Hreflang Tags */}
        <link rel="alternate" hreflang="en-in" href={metaUrl} />
        <link rel="alternate" hreflang="en-us" href={metaUrl} />
        <link rel="alternate" hreflang="en-gb" href={metaUrl} />
        <link rel="alternate" hreflang="x-default" href={metaUrl} />

        {/* Canonical URL */}
        <link rel="canonical" href={metaUrl} />

        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      </Helmet>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes neonPulse {
          0% { opacity: 0.8; }
          100% { opacity: 1; }
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.7; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0.7; }
        }
        
        @keyframes shine {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse 4s ease-in-out infinite;
        }
      `}</style>
      
      {/* Hero Section */}
      <section id="hero" ref={heroRef} className="relative w-screen h-screen overflow-hidden">
        <video
          className="absolute top-0 left-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster={webtech}
        >
          <source src={technologyVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80 flex items-center justify-center px-6">
          <div className="text-center text-white max-w-3xl relative z-10">
            <div className="mb-6">
              <div className="inline-block relative">
                <div className="absolute -inset-1 rounded-full blur-xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-70 animate-pulse"></div>
                <div className="relative px-6 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-white/10">
                  <span className="text-sm font-medium tracking-wider uppercase">Next-Gen Tech Solutions</span>
                </div>
              </div>
            </div>
            
            <h1 
              className="text-3xl md:text-5xl font-bold mb-4 leading-tight"
              style={{
                textShadow: '0 0 10px rgba(111, 76, 255, 0.7), 0 0 20px rgba(111, 76, 255, 0.5)'
              }}
              itemProp="headline"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                Innovative Technology Solutions
              </span>
              <br />
              <span className="text-white">for Business Growth</span>
            </h1>
            
            <p className="mt-4 text-lg md:text-xl leading-relaxed text-gray-300" itemProp="description">
              At <span className="font-semibold text-blue-300">Techecosolution</span>, we
              specialize in delivering advanced technology solutions tailored to
              your business needs.
            </p>
            
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <GlowButton text="Explore Services" href="#web" primary={true} />
              <GlowButton text="Why Choose Us" href="#whyUs" primary={false} />
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
          <a href="#web" className="flex flex-col items-center text-white opacity-80 hover:opacity-100 transition-opacity" aria-label="Scroll to services section">
            <span className="mb-2 text-sm">Scroll Down</span>
            <div className="relative">
              <div className="absolute -inset-1 rounded-full blur-md bg-indigo-500 opacity-70 animate-pulse"></div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6 relative"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                style={{ filter: "drop-shadow(0 0 8px rgba(111, 76, 255, 0.8))" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </a>
        </div>
      </section>

      {/* Web Development Section */}
      <section 
        id="web" 
        ref={webRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-blue-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="Web Development" 
              color="#4fd1c5" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="Web Development Services" />
            <meta itemProp="serviceType" content="Web Development" />
            
            <div className="space-y-3">
              {[
                "Transforming ideas into visually stunning and highly functional websites.",
                "Cutting-edge designs ensuring seamless user interaction across all devices.",
                "Engineered for top search rankings with modern SEO strategies.",
                "Secure, lightning-fast, and built for the future of digital experiences."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#4fd1c5">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Professional web development services creating stunning, responsive websites with modern SEO strategies and cutting-edge design." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#4fd1c5">
              <img 
                src={webtech} 
                alt="Web Development Services - Responsive Website Design and Development" 
                className="w-full max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>

      {/* E-Commerce Development Section */}
      <section 
        id="ecommerce" 
        ref={ecommerceRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-pink-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row-reverse items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="E-Commerce Development" 
              color="#fc5296" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="E-Commerce Development Services" />
            <meta itemProp="serviceType" content="E-Commerce Development" />
            
            <div className="space-y-3">
              {[
                "Powerful online stores with secure payment gateways, inventory management, and conversion-optimized design.",
                "CMS Integration (WordPress, Shopify, etc.) for effortless website management.",
                "Custom web applications designed to enhance business productivity.",
                "Ongoing support & maintenance with regular updates and performance monitoring.",
                "Let's Build Together - Transform your digital presence with a website that drives engagement and growth."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#fc5296">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Professional e-commerce development services with secure payment gateways, inventory management, and CMS integration for online business success." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#fc5296">
              <img 
                src={ecommerce} 
                alt="E-Commerce Development Services - Online Store Development and Payment Integration" 
                className="w-full h-auto max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>

      {/* Software Development Section */}
      <section 
        id="software" 
        ref={softwareRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-teal-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="Software Development"
              color="#4fd1c5" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="Custom Software Development Services" />
            <meta itemProp="serviceType" content="Software Development" />
            
            <div className="space-y-3">
              {[
                "From concept to deployment, we handle every phase with precision and expertise.",
                "Software solutions that fit your specific business requirements for enhanced efficiency.",
                "Designed to evolve as your business grows and technology advances.",
                "Smoothly integrate with existing platforms, third-party APIs, and enterprise applications.",
                "Enterprise-grade security standards to protect sensitive data."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#4fd1c5">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Custom software development solutions from concept to deployment with enterprise-grade security and seamless integration capabilities." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#4fd1c5">
              <img 
                src={softwareTech} 
                alt="Custom Software Development Services - Enterprise Software Solutions" 
                className="w-full max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>

      {/* Mobile App Development Section */}
      <section 
        id="mobile" 
        ref={mobileRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-yellow-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row-reverse items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="Mobile App Development" 
              color="#feca57" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="Mobile App Development Services" />
            <meta itemProp="serviceType" content="Mobile App Development" />
            
            <div className="space-y-3">
              {[
                "Bespoke mobile solutions tailored to your business needs.",
                "Developing apps for iOS, Android, and hybrid frameworks (Flutter, React Native, etc.).",
                "Creating visually appealing and easy-to-navigate apps that boost engagement.",
                "Delivering lightweight, fast, and responsive mobile applications.",
                "Apps that grow with your business, integrating new features without disruption."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#feca57">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Professional mobile app development for iOS, Android, and hybrid platforms with responsive design and seamless user experience." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#feca57">
              <img 
                src={mobileApp} 
                alt="Mobile App Development Services - iOS and Android App Development" 
                className="w-full h-auto max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>

      {/* Cloud Solutions Section */}
      <section 
        id="cloud" 
        ref={cloudRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-indigo-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="Cloud Solutions" 
              color="#6f4cff" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="Cloud Solutions Services" />
            <meta itemProp="serviceType" content="Cloud Computing" />
            
            <div className="space-y-3">
              {[
                "Flexible cloud environments tailored to business needs.",
                "Save costs with pay-as-you-go pricing models.",
                "Effortlessly transition from on-premise to cloud platforms.",
                "99.9% uptime with disaster recovery and backup solutions.",
                "Cloud solutions with data encryption and regulatory compliance."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#6f4cff">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Comprehensive cloud solutions with 99.9% uptime, disaster recovery, and enterprise-grade security for business digital transformation." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#6f4cff">
              <img 
                src={cloudSolutions} 
                alt="Cloud Solutions Services - Cloud Computing and Migration Services" 
                className="w-full h-auto max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>

      {/* Data Analytics Section */}
      <section 
        id="data" 
        ref={dataRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Service"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-pink-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col md:flex-row-reverse items-center relative z-10">
          <div className="md:w-1/2 text-left">
            <NeonText 
              text="Data Analytics" 
              color="#fc5296" 
              size="text-3xl lg:text-4xl"
              className="mb-4 tracking-wide uppercase"
            />
            <meta itemProp="name" content="Data Analytics Services" />
            <meta itemProp="serviceType" content="Data Analytics" />
            
            <div className="space-y-3">
              {[
                "Turn raw data into valuable insights to drive decision-making.",
                "Leverage AI and machine learning to analyze complex data patterns.",
                "Tailored dashboards and reports to meet your unique needs.",
                "Use historical data to forecast trends and improve strategy.",
                "Get instant analytics to stay ahead of competitors."
              ].map((text, index) => (
                <div key={index}>
                  <GlowingCard color="#fc5296">
                    <p className="text-base text-gray-300">{text}</p>
                  </GlowingCard>
                </div>
              ))}
            </div>
            <meta itemProp="description" content="Advanced data analytics services using AI and machine learning to transform raw data into actionable business insights and forecasting." />
          </div>
          
          <div className="md:w-1/2 flex justify-center mt-8 md:mt-0 relative">
            <TiltGlowCard color="#fc5296">
              <img 
                src={dataAnalytics} 
                alt="Data Analytics Services - AI and Machine Learning Data Analysis" 
                className="w-full h-auto max-w-md md:max-w-lg rounded-lg"
                loading="lazy"
                itemProp="image"
              />
            </TiltGlowCard>
          </div>
        </div>
      </section>
      
      {/* Why Choose Us Section */}
      <section 
        id="whyUs" 
        ref={whyUsRef}
        className="relative w-full min-h-screen bg-black text-white flex items-center overflow-hidden py-20"
        itemScope 
        itemType="https://schema.org/Organization"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-teal-500 mix-blend-screen filter blur-3xl"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 500 + 100}px`,
                height: `${Math.random() * 500 + 100}px`,
                opacity: Math.random() * 0.5,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-6 lg:px-16 flex flex-col items-center relative z-10">
          <NeonText 
            text="Why Choose Techecosolution?" 
            color="#4fd1c5" 
            size="text-3xl lg:text-4xl"
            className="mb-8 tracking-wide uppercase text-center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl">
            {[
              {
                icon: '👨‍💻',
                title: 'Expert Team',
                description: 'Skilled professionals with deep technical expertise.',
                color: '#6f4cff'
              },
              {
                icon: '🔧',
                title: 'Custom & Scalable Solutions',
                description: 'Solutions tailored to your unique business needs.',
                color: '#4fd1c5'
              },
              {
                icon: '🔒',
                title: 'Security & Compliance',
                description: 'Industry-standard security for data protection.',
                color: '#fc5296'
              },
              {
                icon: '🔄',
                title: 'Seamless Integration',
                description: 'Compatible with various platforms and third-party tools.',
                color: '#feca57'
              },
              {
                icon: '⚡',
                title: 'Agile & Transparent Process',
                description: 'Continuous collaboration for best results.',
                color: '#6f4cff'
              },
              {
                icon: '🛠️',
                title: '24/7 Support & Maintenance',
                description: 'Reliable assistance whenever you need it.',
                color: '#4fd1c5'
              }
            ].map((item, index) => (
              <article
                key={index}
                className="opacity-100"
                itemScope
                itemType="https://schema.org/Service"
              >
                <GlowingCard color={item.color}>
                  <div className="flex flex-col items-center text-center">
                    <div className="text-3xl mb-3" role="img" aria-label={item.title}>{item.icon}</div>
                    <h3 className="text-lg font-bold mb-2" itemProp="name">{item.title}</h3>
                    <p className="text-sm text-gray-300" itemProp="description">{item.description}</p>
                  </div>
                </GlowingCard>
              </article>
            ))}
          </div>
          
          <div className="mt-12 text-center max-w-3xl">
            <h3 className="text-xl font-bold mb-3" style={{ 
              textShadow: '0 0 5px #4fd1c5, 0 0 10px #4fd1c5' 
            }}>
              Let's Build the Future Together!
            </h3>
            <p className="text-base text-gray-300 mb-6">
              At Techecosolution, we combine innovation, expertise, and technology to deliver high-quality solutions that help businesses thrive in a digital world.
            </p>
            <GlowButton text="Contact Us" href="/contact-tes" primary={true} />
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnologyPage;